import React, {useState, useEffect} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch, useHistory, useLocation } from "react-router-dom";

import _ from "lodash";
import cx from "classnames";
import { useAccordionButton } from "react-bootstrap/AccordionButton";
import { SLIDE } from "constants/widgetTypes";
import { EditorContext } from "components/Editor";
import {
  Button,
  Card,
  Accordion,
  Popover,
  OverlayTrigger,
} from "react-bootstrap";
import { actionGroup, arrangeWidget, cloneSlider, createWidget, deleteWidgets, switchInteractivePageSection, updateWidgetName, updateWidgetProperty } from "actions/editor";
import shortid from "shortid";
import { IKImage } from "imagekitio-react";
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from "@dnd-kit/sortable";

import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { fetchPage, invalidatePageChange, updatePage } from "actions/pages";
import { HeaderContext } from "pages/pages/components/header";
import { getBaseUrl } from "services/util";
import storyboardRoleService from "services/storyboardRoles.service";

import styles from "./pageDetails.module.scss";

function CustomToggle({ children, eventKey, currentPage }) {
  const dispatch = useDispatch();
  const toggleAccordion = useAccordionButton(eventKey, () => console.log(''));
  const pages = useSelector((state) => { return _.get(state, `pages.page`)});

  const decoratedOnClick = (event) => {
    if (!_.has(pages, eventKey)) {
      dispatch(fetchPage(eventKey));
      event.preventDefault();
    } 
    toggleAccordion(event)
  };

  return (
    <div className={styles.detailTag} onClick={decoratedOnClick}>
      <div
        className={cx(styles.actionWrapper, {
          [styles.active]: currentPage === eventKey,
        })}
      >
        <span className={styles.name}>{children}</span>
        <Button className={styles.expandButton} variant="info">
          <i className={cx("cleverstory-drop-down")}></i>
        </Button>
      </div>
    </div>
  );
}

const PageDetailCard = ({key, id, eventKey, page, currentPage}) => {

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({id});

  const style = { 
    transform: transform ? CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }) : undefined, 
    transition,
    zIndex: isDragging ? 9999 : 'auto',
    position: isDragging ? 'relative' : 'static',
  };

  useEffect(() => {
    if (isDragging){
      const selecto = editorContext.editorSession.selecto.current;
      const selectoElement = document.querySelector(`.${selecto.selectionElement.classList[0]}`);
      selectoElement.setAttribute("style", "display: none !important;");
    }
  },[isDragging])

  const slideWidgets = useSelector((state) => {
    const widgets = currentPage === eventKey ? _.get(state, `${editor}.present.widgets`, []) : _.get(state, `pages.page.${eventKey}.item.draft.widgets`, [])
    const slides = widgets.filter(
      (item) => item.type === SLIDE && _.get(item, "properties.desktop.visible")
    );
    return _.sortBy(slides, "order")
  });

  const loading = useSelector((state) => { 
    if(currentPage === eventKey){
      return false;
    }
    return _.get(state, `pages.page.${eventKey}.loading`)
  });

  return (
    <Card key={key} className={cx(styles.pageCard)} ref={setNodeRef} style={style}  {...attributes}>
      <Card.Header>
        <i className={cx(styles.draggable,'cleverstory-drag', {[styles.activeDraggable]: currentPage === eventKey})} {...listeners} />
        <CustomToggle currentPage={currentPage} eventKey={eventKey}>
          <TruncatedTextWithTooltip placement="top" maxWidth={150} content={_.get(page, "name")} />
        </CustomToggle>
      </Card.Header>
      <Accordion.Collapse appear={currentPage == eventKey} className={styles.accordCollapse} eventKey={eventKey} style={{display: isDragging ? "none" : ""}}> 
        <Card.Body className={styles.slideViewWrapper}>
          { loading &&
            <span style={{color: '#ffffff', fontSize: '0.75em'}}>Loading Slides.....</span>
          }
          {_.map(slideWidgets, (slide, index) => {
            return (
              <SlideCardView 
                pageId={eventKey}
                slideWidgets={slideWidgets} 
                editor={editor} 
                active={currentPage == eventKey}
                slide={slide} 
                key={index}
              />
            );
          })}
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
};

const SlideCardView = ({pageId, slideWidgets, editor, slide, key, active}) => {
  const dispatch = useDispatch();
  const inputRef = React.useRef(null);
  const history = useHistory();

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);
  const { id } = useParams();

  const [editable, setEditable] = useState(false);
  const [inputName, setInputName] = useState(slide.name);
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const activePageSection = useSelector((state) =>
    _.get(state, `${editor}.present.interactive.active`)
  );
  const iconUrl = _.get(slide, 'icon.url');
  const slidesCountCondition = _.size(slideWidgets) > 1;
  const firstSlide = _.get(slideWidgets, '[0].uid') === _.get(slide, 'uid');
  const lastSlide = _.get(_.last(slideWidgets), 'uid') === _.get(slide, 'uid');
  const orderState = useSelector(state => {
    if (!slide) {
      return {
        back: false,
        front: false
      };
    }
    var widgets = _.get(state, `${editor}.present.widgets`) || [];
    widgets = widgets.filter(item => item.type === SLIDE);
    widgets = _.orderBy(widgets, 'order', 'desc');

    const index = widgets.findIndex(item => item.uid === slide.uid);
    const total = _.size(widgets);

    return {
      back: index < total - 1,
      front: index > 0,
      index: index,
      total: total
    };
  });

  const widgetsList = useSelector((state) =>
  _.get(state, `${editor}.present.widgets`, [])
);


  const handleDeleteSlide =()=> {
    if(activePageSection == slide.uid) {
      const widgets = widgetsList.filter(item => item.type === SLIDE);
      const orderedWidgets = _.orderBy(widgets, 'order', 'desc');
      const currentIndex = orderedWidgets.findIndex(widget => widget.uid === slide.uid);
      if (currentIndex === orderedWidgets.length - 1) {
        const previousSlide = orderedWidgets[currentIndex - 1];
        if (previousSlide) {
          dispatch(switchInteractivePageSection(editor, previousSlide.uid));
        }
      } else {
        const nextSlide = orderedWidgets[currentIndex + 1];
        if (nextSlide) {
          dispatch(switchInteractivePageSection(editor, nextSlide.uid));
        }
      }
    }
    dispatch(deleteWidgets(editor, [slide.uid]))
    handleOverlay();
  }

  const handleSlideClick =(event)=> {
    actionGroup.start()
    if(id !== pageId) {
      dispatch(invalidatePageChange(pageId));
      history.push(`${baseUrl}/pages/${pageId}`)
    }
    dispatch(switchInteractivePageSection(editor, slide.uid));
    actionGroup.end()
    event.preventDefault();
  }

  const cloneSlide = () => {
    dispatch(cloneSlider(editor, activePageSection));
  };

  const moveUpward = () => {
    if (!slide.order) {
      return;
    }

    dispatch(arrangeWidget(editor, slide, orderState.index + 1));
    handleOverlay()
  }

  const moveDownward = () => {
    if (!slide.order) {
      return;
    }

    dispatch(arrangeWidget(editor, slide, orderState.index - 1));
    handleOverlay()
  }


  const popover = (
    <Popover id="popover-page-action" className={styles.pageSettingsPopover}>
      <Popover.Body className={styles.pageSettingsPopoverBody}>
        <div className={styles.pageSettingsPopoverContent}>
          <span onClick={() => { setEditable(true) }} >Rename</span>
          { orderState.total > 1 && storyboardRoleService.canManageStoryboard(storyboard) &&
            <span onClick={handleDeleteSlide}>Delete</span>
          }
          <span onClick={cloneSlide}>Clone</span>
          {(slidesCountCondition && !firstSlide) && <span onClick={moveUpward}>Move Up</span>}
          {(slidesCountCondition && !lastSlide) && <span onClick={moveDownward}>Move Down</span>}
        </div>
      </Popover.Body>
    </Popover>
  );

  useEffect(() => {
    if (editable) {
      inputRef.current.focus();
    }
  }, [editable]);

  const _cleanData = (widget) => {
    const children = widget.widgets;
    if (_.size(children) > 0) {
      widget.widgets = children.map((child) => _cleanData(child));
    }

    return _.omit(widget, ["id", "uid", "parent", "template", "icon"]);
  };

  const handleCreateNewSlide = () => {

    var cloned = _.cloneDeep(slide);
    cloned = _cleanData(cloned);
    var uid = `widget_${shortid.generate()}`,
      cloned = {
        ...cloned,
        name: `Slide ${_.size(slideWidgets) + 1}`,
        uid: uid,
      };

    var widgets = widgetsList
    widgets = widgets.filter(item => item.type === SLIDE);
    widgets = _.orderBy(widgets, 'order', 'desc');
  
    const index = widgets.findIndex(item => item.uid === slide.uid);
    actionGroup.start();
    dispatch(createWidget(editor, [cloned], null, false));
    dispatch(arrangeWidget(editor, cloned, index))
    actionGroup.end();
  };

  const handleNameChange = (e) => {
    setInputName(e.target.value);
  }

  const updateContentName = (uid) => {

    dispatch(updateWidgetName(editor, uid, inputName));
    setEditable(false);
  }

  const handleOverlay = (e) => {
    document.body.click();
  };

  return (
    <div key={key} className={cx(styles.slideCard, {[styles.activeCard]: slide.uid === activePageSection})}>
      <div onClick={handleSlideClick} className={styles.banner}>
      {iconUrl && (
          <IKImage
            className={styles.icon}
            path={iconUrl}
          />)
        }
        {
          !iconUrl && (<img src={'/images/storyboard.png'} className={styles.icon} />)
        }
      </div>
      <div className={styles.titleAction}>
        {editable &&
          <input
            ref={inputRef}
            type={'text'}
            className={styles.inputFieldName}
            value={inputName}
            onChange={handleNameChange}
            onBlur={() => updateContentName(slide.uid)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateContentName(slide.uid);
              }
            }}
          />
        }

        {!editable &&
          <span className={styles.name}>
            <TruncatedTextWithTooltip content={slide.name} maxWidth={120} placement='top'/>
          </span>
        }
        { active &&       
        <OverlayTrigger
          rootClose={true}
          rootCloseEvent="click"
          trigger="click"
          placement="right"
          overlay={popover}
        >
          <i
            className={cx("cleverstory-more", styles.iconMore)}
            onClick={handleOverlay}
          />
        </OverlayTrigger>}
      </div>
      { active && 
      <div className={styles.bottomContainer}>
        <Button onClick={handleCreateNewSlide} className={styles.addSlides} variant="info">
          <i className={cx("cleverstory-plus", styles.addIcon)}/>
        </Button>
      </div>
      }
    </div>
  );
};

const Handle = React.forwardRef((props, ref) => {
  return (
    <div className={styles.handle} {...props} ref={ref}>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
    </div>
  );
});

export default PageDetailCard;
